"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  useReducedMotion,
} from "motion/react";

/**
 * CastingSheet — 选角样张照片墙（Hero 视觉主体）。
 * 观念：导演摊开的候选照片，一墙候选里一个被「聚光」选中，裂变成千万角色。
 *
 * 布局（关键）：固定基准画布（CANVAS_W×CANVAS_H）内用 px 坐标+px 尺寸摆好不规则构图，
 * 整块画布用 transform: scale() 按容器等比缩放（contain 语义）。
 * 因此图片与间隔一起等比放大/缩小，构图比例在任何屏幕/设备下恒定——不会出现
 * 「大屏间隔被拉大、图片却不长大」的问题。
 *
 * 交互（戏剧化聚焦 = 选角动作）：
 * - hover 任意卡 → 该卡放大、全彩点亮、强 rim light、置顶；其余卡缩小、暗下、去饱和退让（聚光灯）。
 * - 无 hover：焦点在卡间缓慢自动巡游；所有卡极缓慢多相位浮动，让墙「活着」。
 * - 指针视差：整墙随指针轻移（在缩放坐标系内自动随之缩放）。
 * - reduced-motion：全部静止，保留一张静态选中。
 */

const POOL = Array.from({ length: 24 }, (_, i) => `/avatars/wall-${i + 1}.jpg`);

// 基准画布尺寸（px）：所有卡坐标/尺寸相对它，缩放时比例恒定。
const CANVAS_W = 760;
const CANVAS_H = 780;
// 缩放上下限：超宽屏不无限放大抢文字，小屏不缩到糊。
const MIN_SCALE = 0.6;
const MAX_SCALE = 1.55;

type Slot = { img: string; x: number; y: number; w: number; depth: number; rot: number; float: number };
// x/y/w 均为基准画布内的 px。aspect 3/4，故卡高 = w*4/3。
// 刻意错落：大小交错、位置不对齐、疏密有节奏；大卡锚点一上一下，重心居中。
const SLOTS: Slot[] = [
  { img: POOL[0], x: 144, y: 23, w: 176, depth: 0.88, rot: -2, float: 0 },
  { img: POOL[16], x: 331, y: 8, w: 128, depth: 0.54, rot: 2, float: 2.7 },
  { img: POOL[3], x: 461, y: 38, w: 146, depth: 0.6, rot: 3, float: 1.3 },
  { img: POOL[6], x: 619, y: 15, w: 122, depth: 0.48, rot: 4, float: 3.2 },
  { img: POOL[19], x: 14, y: 213, w: 132, depth: 0.54, rot: 2, float: 2.3 },
  { img: POOL[9], x: 173, y: 274, w: 172, depth: 0.82, rot: -3, float: 0.6 },
  { img: POOL[13], x: 590, y: 243, w: 130, depth: 0.52, rot: 3, float: 3.8 },
  { img: POOL[2], x: 432, y: 304, w: 158, depth: 0.72, rot: -3, float: 2.9 },
  { img: POOL[15], x: 43, y: 441, w: 136, depth: 0.6, rot: 2, float: 1.5 },
  { img: POOL[18], x: 317, y: 395, w: 180, depth: 0.92, rot: 1, float: 1.8 },
  { img: POOL[8], x: 605, y: 426, w: 124, depth: 0.5, rot: 4, float: 4.2 },
  { img: POOL[21], x: 173, y: 517, w: 138, depth: 0.6, rot: -2, float: 4.0 },
  { img: POOL[12], x: 490, y: 547, w: 146, depth: 0.68, rot: -2, float: 2.6 },
  { img: POOL[11], x: 29, y: 608, w: 118, depth: 0.46, rot: 3, float: 3.4 },
  { img: POOL[4], x: 634, y: 578, w: 120, depth: 0.5, rot: -4, float: 0.9 },
];
const DEFAULT_SELECTED = 5;

export function CastingSheet() {
  const reduce = useReducedMotion();
  const [hovered, setHovered] = useState<number | null>(null);
  const [auto, setAuto] = useState(DEFAULT_SELECTED);

  // contain 等比缩放：ResizeObserver 读容器尺寸，算 scale（仅尺寸变化时触发，无每帧开销）
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const compute = () => {
      const r = el.getBoundingClientRect();
      if (!r.width || !r.height) return;
      const s = Math.min(r.width / CANVAS_W, r.height / CANVAS_H);
      setScale(Math.max(MIN_SCALE, Math.min(MAX_SCALE, s)));
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const px = useMotionValue(0);
  const py = useMotionValue(0);
  const sx = useSpring(px, { stiffness: 50, damping: 20 });
  const sy = useSpring(py, { stiffness: 50, damping: 20 });

  useEffect(() => {
    if (reduce) return;
    const onMove = (e: PointerEvent) => {
      px.set((e.clientX / window.innerWidth) * 2 - 1);
      py.set((e.clientY / window.innerHeight) * 2 - 1);
    };
    window.addEventListener("pointermove", onMove, { passive: true });
    return () => window.removeEventListener("pointermove", onMove);
  }, [reduce, px, py]);

  useEffect(() => {
    if (hovered !== null || reduce) return;
    const id = setInterval(() => setAuto((a) => (a + 1) % SLOTS.length), 3000);
    return () => clearInterval(id);
  }, [hovered, reduce]);

  const selected = hovered ?? auto;
  const focusing = hovered !== null;

  return (
    <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
      {/* 摄影棚双色温打光（暖钨丝 + 冷日光）打在炭灰背景纸上 */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(80% 70% at 82% 8%, rgba(255,168,92,0.16), transparent 60%), radial-gradient(70% 70% at 12% 96%, rgba(60,150,190,0.16), transparent 62%)",
        }}
      />
      {/* 聚焦时全局微压暗，强化「聚光灯」 */}
      <motion.div
        className="pointer-events-none absolute inset-0 bg-[var(--color-void)]"
        animate={{ opacity: focusing ? 0.3 : 0 }}
        transition={{ duration: 0.4 }}
      />

      {/* 照片墙容器：右侧安全区（左侧留给文字，不遮挡），flex 居中承载缩放画布 */}
      <div
        ref={wrapRef}
        className="absolute inset-y-0 right-0 flex w-full items-center justify-center opacity-40 sm:w-[70%] sm:opacity-100 md:w-[62%] lg:w-[58%] xl:w-[56%]"
      >
        {/* 固定基准画布 → 整体等比缩放（图与间隔同步缩放，比例恒定）*/}
        <div
          className="relative"
          style={{
            width: CANVAS_W,
            height: CANVAS_H,
            transform: `scale(${scale})`,
            transformOrigin: "center center",
          }}
        >
          {SLOTS.map((s, i) => (
            <FloatCard
              key={i}
              slot={s}
              index={i}
              selected={selected === i}
              dimmed={hovered !== null && hovered !== i}
              focusing={focusing}
              sx={sx}
              sy={sy}
              reduce={!!reduce}
              onEnter={() => setHovered(i)}
              onLeave={() => setHovered(null)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function FloatCard({
  slot,
  index,
  selected,
  dimmed,
  focusing,
  sx,
  sy,
  reduce,
  onEnter,
  onLeave,
}: {
  slot: Slot;
  index: number;
  selected: boolean;
  dimmed: boolean;
  focusing: boolean;
  sx: ReturnType<typeof useSpring>;
  sy: ReturnType<typeof useSpring>;
  reduce: boolean;
  onEnter: () => void;
  onLeave: () => void;
}) {
  // 视差（基准画布 px，随画布 scale 一起缩放）：近景动得多，远景少
  const amp = 26 * slot.depth;
  const tx = useTransform(sx, [-1, 1], [amp, -amp]);
  const ty = useTransform(sy, [-1, 1], [amp * 0.7, -amp * 0.7]);

  // 戏剧化聚焦：选中放大+全彩+清晰；被压卡明显缩小、暗下、去饱和（聚光灯退让）
  const scale = selected ? 1.12 : dimmed ? 0.9 : 1;
  const opacity = selected ? 1 : dimmed ? 0.34 : 0.72 + slot.depth * 0.28;
  const saturate = selected ? 1.08 : dimmed ? 0.55 : 0.9 + slot.depth * 0.1;
  const brightness = selected ? 1.06 : dimmed ? 0.7 : 0.9 + slot.depth * 0.1;
  const blurPx = selected ? 0 : dimmed ? 2 : slot.depth < 0.5 ? 0.6 : 0;

  return (
    <motion.figure
      onPointerEnter={onEnter}
      onPointerLeave={onLeave}
      className="absolute pointer-events-auto cursor-pointer"
      style={{
        left: slot.x,
        top: slot.y,
        width: slot.w,
        x: reduce ? 0 : tx,
        y: reduce ? 0 : ty,
        // hover 命中可靠：选中/hover 卡置顶，避免被上层卡挡住事件
        zIndex: selected ? 40 : Math.round(slot.depth * 10) + 1,
      }}
      initial={reduce ? false : { opacity: 0, scale: 0.8, y: 40 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.9, delay: index * 0.06, ease: [0.16, 1, 0.3, 1] }}
    >
      {/* 呼吸浮动层（未聚焦时缓慢多相位漂浮，让墙「活着」）*/}
      <motion.div
        animate={
          reduce || focusing ? { y: 0 } : { y: [0, -8 - slot.depth * 6, 0] }
        }
        transition={{
          duration: 7 + slot.float,
          repeat: Infinity,
          ease: "easeInOut",
          delay: slot.float * 0.5,
        }}
      >
        <motion.div
          className="relative overflow-hidden rounded-[var(--radius-card)]"
          animate={{
            opacity,
            scale,
            rotate: selected ? 0 : slot.rot,
            filter: `blur(${blurPx}px) saturate(${saturate}) brightness(${brightness})`,
          }}
          transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          style={{
            aspectRatio: "3 / 4",
            boxShadow: selected
              ? "0 0 0 1.5px rgba(255,255,255,0.7), -14px 10px 40px -10px rgba(60,150,190,0.6), 16px -8px 40px -10px rgba(255,168,92,0.6), 0 24px 60px -16px rgba(0,0,0,0.8)"
              : "var(--shadow-node)",
          }}
        >
          <Image
            src={slot.img}
            alt=""
            fill
            sizes="(max-width: 768px) 30vw, 220px"
            priority={index < 3}
            className="object-cover"
          />
          {/* 摄影棚硬光：左上暖高光 + 右下冷阴影 */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(145deg, rgba(255,180,120,0.14) 0%, transparent 34%, transparent 62%, rgba(20,40,60,0.5) 100%)",
            }}
          />
          <div
            className="absolute inset-0 rounded-[var(--radius-card)]"
            style={{ boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.14)" }}
          />
          {selected && <SelectedFrame reduce={reduce} />}
        </motion.div>
      </motion.div>
    </motion.figure>
  );
}

/** 选中态：影棚主光——旋转 gel 光环描边（CSS mask 贴合圆角）+ 标注 */
function SelectedFrame({ reduce }: { reduce: boolean }) {
  return (
    <>
      <div
        className="absolute inset-0 overflow-hidden rounded-[var(--radius-card)]"
        style={{
          padding: "2px",
          WebkitMask:
            "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
          WebkitMaskComposite: "xor",
          maskComposite: "exclude",
        }}
      >
        <div
          className={`absolute left-1/2 top-1/2 aspect-square w-[180%] -translate-x-1/2 -translate-y-1/2 ${
            reduce ? "" : "animate-gel-spin"
          }`}
          style={{
            background:
              "conic-gradient(from 0deg, #ffa85c, #ffffff, #3c96be, #1b1e24, #ffa85c)",
          }}
        />
      </div>
      <div
        className="absolute inset-[2px] rounded-[calc(var(--radius-card)-2px)]"
        style={{ boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.28)" }}
      />
      <div className="absolute left-2 top-2">
        <span
          className="inline-flex items-center rounded-[var(--radius-pill)] px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--color-cool)]"
          style={{
            background: "color-mix(in oklab, var(--color-void) 78%, transparent)",
            boxShadow: "inset 0 0 0 1px color-mix(in oklab, var(--color-cool) 45%, transparent)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
          }}
        >
          casting
        </span>
      </div>
    </>
  );
}
